"use client";

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import Link from "next/link";
import { useRouter } from "next/navigation";
import type { Tool, ToolField } from "@/lib/types";
import { AnalyticsEvent, trackAnalytics } from "@/lib/analytics";
import { useAuth } from "@/lib/context/AuthProvider";
import { useLocale } from "@/lib/context/LocaleProvider";
import { useProjects } from "@/lib/context/ProjectsProvider";
import { useUI } from "@/lib/context/UIProvider";
import { useHydrated } from "@/lib/hooks/useHydrated";
import { useSimulatedProgress } from "@/lib/hooks/useSimulatedProgress";
import { isApiError, isUsageLimitExceeded } from "@/lib/api/client";
import {
  buildToolRunValues,
  fetchTools,
  mapApiTool,
  runTool,
  type ApiToolRunData,
} from "@/lib/api/tools";
import { formatGenerationDate } from "@/lib/api/generations";
import {
  emptyFormErrors,
  hasFieldErrors,
  resolveServerError,
  type FieldErrors,
  type FormErrors,
} from "@/lib/form/errors";
import type { ProjectItem, ToolRunArtifact } from "@/lib/types";
import { cn } from "@/lib/utils";
import { Badge } from "@/components/ui/Badge";
import { IconTile } from "@/components/ui/ToolIcon";
import { ProgressBar } from "@/components/ui/ProgressBar";
import { FormAlert } from "@/components/ui/FormAlert";
import {
  CheckboxRow,
  FieldError,
  FieldLabel,
  SelectInput,
  TextArea,
  TextInput,
} from "@/components/ui/fields";
import { ChevronRight, Reset, Sparkle } from "@/components/ui/icons";

type BadgeVariant = "pro" | "exp";
type FormValues = Record<string, string | boolean>;

/** Build a local ProjectItem from a guest tool run (API does not link guest projects). */
function guestItemFromRun(
  data: ApiToolRunData,
  title: string,
  toolTitle: string,
  locale: string,
): ProjectItem {
  const now = Date.now();
  const createdAtIso = new Date(now).toISOString();
  const artifacts: ToolRunArtifact[] = (data.result.artifacts ?? []).map(
    (a) => ({
      role: a.role,
      url: a.url,
      fileName: a.fileName,
      mimeType: a.mimeType,
      extension: a.extension,
    }),
  );
  return {
    name: title,
    slug: data.generationSlug,
    tool: toolTitle,
    date: formatGenerationDate(createdAtIso, locale),
    generated: true,
    createdAt: now,
    modality: data.modality,
    markdown:
      data.result.type === "text" ? data.result.markdown : undefined,
    artifacts: artifacts.length > 0 ? artifacts : undefined,
  };
}

/** Primary action. Module-level so it is not redefined on every render. */
function GenerateButton({
  full,
  label,
  disabled,
  busy,
  onGenerate,
}: {
  full?: boolean;
  label: string;
  disabled: boolean;
  busy: boolean;
  onGenerate: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onGenerate}
      disabled={disabled}
      aria-busy={busy || undefined}
      className={cn(
        "flex items-center justify-center gap-[7px] rounded-full bg-accent font-semibold text-white transition hover:brightness-105 disabled:cursor-default disabled:opacity-45",
        full ? "h-12 flex-[1.5] text-[14.5px]" : "h-[42px] flex-[1.4] text-[13.5px]",
      )}
    >
      <Sparkle size={full ? 16 : 15} />
      <span>{label}</span>
    </button>
  );
}

/** Secondary action beside `GenerateButton`. */
function ResetButton({
  full,
  label,
  disabled,
  onReset,
}: {
  full?: boolean;
  label: string;
  disabled: boolean;
  onReset: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onReset}
      disabled={disabled}
      className={cn(
        "flex flex-1 items-center justify-center gap-[7px] rounded-full border border-[rgba(26,24,22,0.16)] bg-white font-semibold text-ink transition-colors hover:bg-[rgba(26,24,22,0.04)] disabled:cursor-default disabled:opacity-45",
        full ? "h-12 text-[14.5px]" : "h-[42px] text-[13.5px]",
      )}
    >
      {!full ? <Reset size={15} /> : null}
      <span>{label}</span>
    </button>
  );
}

function badgesFor(field: ToolField): BadgeVariant[] {
  const badges: BadgeVariant[] = [];
  if (field.isPro) badges.push("pro");
  if (field.isExperimental) badges.push("exp");
  return badges;
}

function defaultValues(fields: ToolField[], projectId?: string): FormValues {
  const next: FormValues = { title: "" };
  if (projectId) next.project = projectId;
  for (const field of fields) {
    if (field.type === "checkbox") {
      next[field.slug] = field.isDefaultChecked ?? false;
    } else if (field.type === "select") {
      const def = field.options.find((o) => o.isDefault) ?? field.options[0];
      next[field.slug] = def ? String(def.id) : "";
    } else {
      next[field.slug] = "";
    }
  }
  return next;
}

function isFieldFilled(field: ToolField, values: FormValues): boolean {
  if (field.type === "checkbox") return !!values[field.slug];
  return String(values[field.slug] ?? "").trim().length > 0;
}

/** Interactive body of the tool-detail page: the form + generate flow. */
export function ToolForm({ tool }: { tool: Tool }) {
  const { t, locale, hydrated: localeHydrated } = useLocale();
  const { isAuthenticated, isPro } = useAuth();
  const { openProModal, openAuthModal } = useUI();
  const {
    projectChoices,
    getProjectById,
    ensureProjectCatalog,
    addGeneratedItem,
  } = useProjects();
  const router = useRouter();
  const {
    progress,
    start: startProgress,
    complete: completeProgress,
    reset: resetProgress,
  } = useSimulatedProgress();
  const abortRef = useRef<AbortController | null>(null);

  const apiFields = useMemo(
    () => [...(tool.fields ?? [])].sort((a, b) => a.sortOrder - b.sortOrder),
    [tool.fields],
  );

  const projectOptions = projectChoices.map((p) => ({
    value: p.id,
    label: p.name,
  }));
  const defaultProjectId = projectOptions[0]?.value;

  const [values, setValues] = useState<FormValues>(() =>
    defaultValues(apiFields, defaultProjectId),
  );
  const [generating, setGenerating] = useState(false);
  const [errors, setErrors] = useState<FormErrors>(emptyFormErrors);
  const [related, setRelated] = useState<Tool[]>([]);
  const portalReady = useHydrated();
  const errorRef = useRef<HTMLDivElement | null>(null);

  useEffect(
    () => () => {
      abortRef.current?.abort();
    },
    [],
  );

  // Broader project list for the save-to select (API catalog; guests use local bag).
  useEffect(() => {
    ensureProjectCatalog();
  }, [ensureProjectCatalog]);

  // Re-seed form defaults when the tool or locale (and thus fields) change, and
  // fill Save-to-project once the catalog resolves. Both adjust state during
  // render rather than in an effect, so there is no extra pass with stale values
  // (https://react.dev/learn/you-might-not-need-an-effect).
  const seedKey = `${tool.slug}|${locale}|${isAuthenticated}|${apiFields
    .map((f) => f.slug)
    .join(",")}`;
  const [seededKey, setSeededKey] = useState(seedKey);

  if (seededKey !== seedKey) {
    setSeededKey(seedKey);
    setValues(defaultValues(apiFields, defaultProjectId));
    setErrors(emptyFormErrors());
  } else if (defaultProjectId && !values.project) {
    setValues({ ...values, project: defaultProjectId });
  }

  // Load other tools in the same category from GET /v1/tool.
  useEffect(() => {
    /* istanbul ignore next -- locale hydration resolves before the first client paint */
    if (!localeHydrated) return;

    let cancelled = false;

    (async () => {
      try {
        const items = await fetchTools(locale);
        if (cancelled) return;
        const sameCategory = items
          .filter((item) => {
            if (item.slug === tool.slug) return false;
            if (tool.categorySlug) return item.category.slug === tool.categorySlug;
            return item.category.name === tool.category;
          })
          .sort((a, b) => a.sortOrder - b.sortOrder)
          .map(mapApiTool);
        setRelated(sameCategory);
      } catch (err) {
        console.error("Failed to load related tools", err);
        if (!cancelled) setRelated([]);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [locale, localeHydrated, tool.slug, tool.category, tool.categorySlug]);

  const setField = (id: string, value: string | boolean) => {
    setValues((prev) => ({ ...prev, [id]: value }));
    setErrors((prev) => {
      if (!prev.fields[id] && !prev.form) return prev;
      const fields = { ...prev.fields };
      delete fields[id];
      return { form: null, fields };
    });
  };

  // Required Pro fields stay locked for free/guest — don't block Create.
  const requiredFields = apiFields.filter((f) => f.isRequired && (isPro || !f.isPro));
  const canGenerate = !generating;

  const reset = () => {
    setErrors(emptyFormErrors());
    setValues(defaultValues(apiFields, defaultProjectId));
  };

  const cancelGenerate = useCallback(() => {
    abortRef.current?.abort();
    abortRef.current = null;
    resetProgress();
    setGenerating(false);
  }, [resetProgress]);

  // Lock body scroll while the centered progress overlay is open; Escape cancels.
  useEffect(() => {
    if (!generating) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") cancelGenerate();
    };
    document.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = prev;
      document.removeEventListener("keydown", onKey);
    };
  }, [generating, cancelGenerate]);

  const generate = async () => {
    /* istanbul ignore next -- the Generate buttons are disabled while a run is in flight */
    if (!canGenerate) return;

    if (tool.isPro && !isPro) {
      openProModal();
      return;
    }

    const fieldErrors: FieldErrors = {};
    for (const field of requiredFields) {
      if (!isFieldFilled(field, values)) {
        fieldErrors[field.slug] = t.validationFieldRequired.replace(
          "{field}",
          field.label,
        );
      }
    }
    if (!String(values.title ?? "").trim()) {
      fieldErrors.title = t.hiTitleRequired;
    }
    if (!String(values.project ?? defaultProjectId ?? "").trim()) {
      fieldErrors.project = t.tfNeedProject;
    }
    if (hasFieldErrors(fieldErrors)) {
      setErrors({
        form: Object.keys(fieldErrors).length > 1 ? t.formCheckFields : null,
        fields: fieldErrors,
      });
      requestAnimationFrame(() => {
        errorRef.current?.scrollIntoView({ behavior: "smooth", block: "nearest" });
      });
      return;
    }

    /* istanbul ignore next -- field validation above already rejects a run without a project */
    if (!defaultProjectId) {
      setErrors({ form: t.tfNeedProject, fields: {} });
      return;
    }

    abortRef.current?.abort();
    const abort = new AbortController();
    abortRef.current = abort;

    setGenerating(true);
    setErrors(emptyFormErrors());
    startProgress();

    try {
      /* istanbul ignore next -- the controller cannot abort between creating it and this line */
      if (abort.signal.aborted) return;

      const project = getProjectById(
        String(values.project ?? defaultProjectId ?? ""),
      );

      if (!project?.slug) {
        resetProgress();
        setErrors({ form: t.tfNeedProject, fields: { project: t.tfNeedProject } });
        setGenerating(false);
        return;
      }

      const runValues = buildToolRunValues(apiFields, values, isPro);
      const draftTitle = String(values.title ?? "").trim();
      // Guests: omit projectSlug/title — API has no local project ids; attach after run.
      const { data } = await runTool(
        tool.slug,
        {
          values: runValues,
          ...(isAuthenticated ? { projectSlug: project.slug } : {}),
          ...(isAuthenticated && draftTitle ? { title: draftTitle } : {}),
        },
        { locale, signal: abort.signal },
      );
      if (abort.signal.aborted) return;

      if (!isAuthenticated) {
        addGeneratedItem(
          project.id,
          guestItemFromRun(data, draftTitle, tool.name, locale),
        );
      }

      trackAnalytics(AnalyticsEvent.ToolRun, {
        tool_slug: tool.slug,
        tool_name: tool.name,
        is_authenticated: isAuthenticated,
      });

      await completeProgress();
      /* istanbul ignore next -- guards a race the API mock cannot reproduce deterministically */
      if (abort.signal.aborted) return;

      router.push(`/history/${data.generationSlug}`);
    } catch (err) {
      if (
        abort.signal.aborted ||
        (err instanceof DOMException && err.name === "AbortError") ||
        (err instanceof Error && err.name === "AbortError")
      ) {
        return;
      }

      resetProgress();
      setGenerating(false);

      // Expected free-tier / plan gate — never surface the raw English API message.
      if (isUsageLimitExceeded(err)) {
        setErrors({
          form: isAuthenticated ? t.tfUsageLimitFree : t.tfUsageLimitGuest,
          fields: {},
        });
        // Defer modal so the localized alert paints first.
        requestAnimationFrame(() => {
          errorRef.current?.scrollIntoView({ behavior: "smooth", block: "nearest" });
          if (isAuthenticated) openProModal();
          else openAuthModal();
        });
        return;
      }

      if (!isApiError(err)) console.error("Tool run failed", err);
      setErrors(resolveServerError(err, t, { fallback: t.tfRunError }));
      requestAnimationFrame(() => {
        errorRef.current?.scrollIntoView({ behavior: "smooth", block: "nearest" });
      });
    } finally {
      if (abortRef.current === abort) abortRef.current = null;
    }
  };

  const renderBadges = (badges: BadgeVariant[]) =>
    badges.map((b) => (
      <Badge
        key={b}
        variant={b}
        size="sm"
        label={b === "exp" ? t.badgeExp : t.badgePro}
      />
    ));

  const progressOverlay =
    generating && portalReady
      ? createPortal(
          <div
            // Above FloatingDock (z-60) and mobile tool actions (z-60); matches AuthModal.
            className="fixed inset-0 z-[85] flex items-center justify-center p-5"
            role="dialog"
            aria-modal="true"
            aria-labelledby="tf-generating-title"
          >
            <button
              type="button"
              aria-label={t.tfCancelRequest}
              onClick={cancelGenerate}
              className="absolute inset-0 animate-fade-in cursor-pointer bg-[rgba(38,33,40,0.26)] backdrop-blur-[14px] backdrop-saturate-[1.25]"
            />
            <div className="card-shadow relative z-[1] w-full max-w-[360px] animate-modal-in rounded-[20px] border border-[rgba(26,24,22,0.08)] bg-modal px-6 pb-5 pt-6">
              <div className="flex items-baseline justify-between gap-3">
                <span
                  id="tf-generating-title"
                  className="text-[15px] font-semibold tracking-[-0.2px] text-ink"
                >
                  {t.tfGenerating}
                </span>
                <span className="text-[13px] tabular-nums text-muted">
                  {Math.round(progress)}%
                </span>
              </div>
              <ProgressBar
                value={progress}
                label={t.tfGenerating}
                className="mt-3 h-1.5"
              />
              <p className="mt-3 text-[13px] leading-[1.45] text-muted">
                {t.tfGeneratingHint}
              </p>
              <button
                type="button"
                onClick={cancelGenerate}
                className="mt-5 flex h-[42px] w-full items-center justify-center rounded-full border border-[rgba(26,24,22,0.16)] bg-white text-[13.5px] font-semibold text-ink transition-colors hover:bg-[rgba(26,24,22,0.04)]"
              >
                {t.tfCancelRequest}
              </button>
            </div>
          </div>,
          document.body,
        )
      : null;

  return (
    <>
      {progressOverlay}

      <div className="mb-3 flex items-center gap-[15px]">
        <div className={cn("relative flex-none", tool.isPro && "pb-1")}>
          <IconTile category={tool.category} icon={tool.icon} size="lg" />
          {tool.isPro ? (
            <span className="pointer-events-none absolute bottom-[3px] left-1/2 -translate-x-1/2 rounded-full border border-gold bg-accent px-[5px] py-[1.5px] text-[7.5px] font-bold leading-none tracking-[0.5px] text-white shadow-[0_1px_2px_rgba(26,24,22,0.18)]">
              {t.badgePro}
            </span>
          ) : null}
        </div>
        <h1 className="min-w-0 text-[28px] font-medium leading-[1.1] tracking-[-1px]">
          {tool.name}
        </h1>
      </div>
      <p className="mb-5 text-[15px] leading-[1.5] text-ink-3 [text-wrap:pretty]">
        {tool.desc}
      </p>

      <div className="flex flex-wrap items-start gap-[26px]">
        {/* Form */}
        <div className="min-w-0 flex-[999_1_440px]">
          {errors.form ? (
            <FormAlert ref={errorRef} className="mb-4">
              {errors.form}
            </FormAlert>
          ) : hasFieldErrors(errors.fields) ? (
            <div ref={errorRef} className="mb-4" aria-hidden="true" />
          ) : null}

          <div
            className={cn(
              "card-shadow mb-4 rounded-[20px] border border-[rgba(26,24,22,0.08)] bg-white p-6 transition-opacity",
              generating && "pointer-events-none opacity-55",
            )}
            aria-disabled={generating || undefined}
          >
            <div className="relative mb-[18px]">
              <FieldLabel htmlFor="tf-title">
                <span>{t.tfTitleLabel}</span>
                <span className="font-medium text-accent">· {t.cpRequired}</span>
              </FieldLabel>
              <TextInput
                id="tf-title"
                value={String(values.title ?? "")}
                placeholder={t.tfTitlePh}
                disabled={generating}
                invalid={Boolean(errors.fields.title)}
                aria-required
                onChange={(e) => setField("title", e.target.value)}
              />
              <FieldError>{errors.fields.title}</FieldError>
            </div>
            <div className="relative mb-0">
              <FieldLabel htmlFor="tf-project">
                <span>{t.tfProjectLabel}</span>
                <span className="font-medium text-accent">· {t.cpRequired}</span>
              </FieldLabel>
              <SelectInput
                id="tf-project"
                value={String(values.project ?? defaultProjectId ?? "")}
                options={projectOptions}
                disabled={generating}
                invalid={Boolean(errors.fields.project)}
                aria-required
                onChange={(next) => setField("project", next)}
              />
              <FieldError>{errors.fields.project}</FieldError>
            </div>
          </div>

          <div
            className={cn(
              "card-shadow rounded-[20px] border border-[rgba(26,24,22,0.08)] bg-white p-6 transition-opacity",
              generating && "pointer-events-none opacity-55",
            )}
            aria-disabled={generating || undefined}
          >
            {apiFields.map((field) => {
              const badges = badgesFor(field);
              const locked = !isPro && field.isPro;
              const fieldDisabled = locked || generating;
              const fieldError = errors.fields[field.slug];

              if (field.type === "checkbox") {
                return (
                  <div key={field.slug} className="relative mb-0.5">
                    <CheckboxRow
                      checked={locked ? false : !!values[field.slug]}
                      disabled={fieldDisabled}
                      onToggle={() => setField(field.slug, !values[field.slug])}
                    >
                      <span>{field.label}</span>
                      {renderBadges(badges)}
                      {field.isRequired ? (
                        <span className="font-medium text-accent">· {t.cpRequired}</span>
                      ) : null}
                    </CheckboxRow>
                    <FieldError>{fieldError}</FieldError>
                    {field.helpText ? (
                      <p className="mb-2 ml-[33px] text-[12px] leading-[1.4] text-muted">
                        {field.helpText}
                      </p>
                    ) : null}
                    {locked && !generating ? (
                      <button
                        type="button"
                        aria-label={t.proModalTitle}
                        onClick={openProModal}
                        className="absolute inset-0 z-[1] cursor-pointer rounded-xl"
                      />
                    ) : null}
                  </div>
                );
              }

              const value = locked
                ? String(
                    field.type === "select"
                      ? (field.options.find((o) => o.isDefault) ?? field.options[0])?.id ?? ""
                      : "",
                  )
                : String(values[field.slug] ?? "");

              return (
                <div key={field.slug} className="relative mb-[18px]">
                  <FieldLabel htmlFor={`tf-${field.slug}`}>
                    <span>{field.label}</span>
                    {renderBadges(badges)}
                    {field.isRequired ? (
                      <span className="font-medium text-accent">· {t.cpRequired}</span>
                    ) : null}
                  </FieldLabel>
                  <div className={locked ? "pointer-events-none opacity-55" : undefined}>
                    {field.type === "input" ? (
                      <TextInput
                        id={`tf-${field.slug}`}
                        value={value}
                        placeholder={field.placeholder}
                        disabled={fieldDisabled}
                        invalid={Boolean(fieldError)}
                        aria-required={field.isRequired || undefined}
                        onChange={(e) => setField(field.slug, e.target.value)}
                      />
                    ) : field.type === "textarea" ? (
                      <TextArea
                        id={`tf-${field.slug}`}
                        rows={6}
                        value={value}
                        placeholder={field.placeholder}
                        disabled={fieldDisabled}
                        invalid={Boolean(fieldError)}
                        aria-required={field.isRequired || undefined}
                        onChange={(e) => setField(field.slug, e.target.value)}
                      />
                    ) : (
                      <SelectInput
                        id={`tf-${field.slug}`}
                        value={value}
                        placeholder={
                          field.placeholder && !field.options.some((o) => o.isDefault)
                            ? field.placeholder
                            : undefined
                        }
                        options={[
                          ...(field.placeholder && !field.options.some((o) => o.isDefault)
                            ? [{ value: "", label: field.placeholder }]
                            : []),
                          ...field.options.map((o) => ({
                            value: String(o.id),
                            label: o.label,
                          })),
                        ]}
                        disabled={fieldDisabled}
                        invalid={Boolean(fieldError)}
                        aria-required={field.isRequired || undefined}
                        onChange={(next) => setField(field.slug, next)}
                      />
                    )}
                  </div>
                  <FieldError>{fieldError}</FieldError>
                  {field.helpText && !fieldError ? (
                    <p className="mt-1.5 text-[12px] leading-[1.4] text-muted">{field.helpText}</p>
                  ) : null}
                  {locked && !generating ? (
                    <button
                      type="button"
                      aria-label={t.proModalTitle}
                      onClick={openProModal}
                      className="absolute inset-0 z-[1] cursor-pointer rounded-[14px]"
                    />
                  ) : null}
                </div>
              );
            })}
          </div>
        </div>

        {/* Aside */}
        <aside className="flex min-w-[270px] flex-[1_1_280px] flex-col md:sticky md:top-[82px] md:self-start">
          <div className="mb-5 hidden gap-2 md:flex">
            <ResetButton label={t.tfReset} disabled={generating} onReset={reset} />
            <GenerateButton
              label={generating ? t.tfGenerating : t.tfGenerate}
              disabled={!canGenerate || generating}
              busy={generating}
              onGenerate={() => void generate()}
            />
          </div>
          <div className="mb-2 text-[11px] font-bold uppercase tracking-[0.7px] text-muted-2">
            {t.tfMoreIn}
          </div>
          <div className="overflow-hidden rounded-[20px] border border-[rgba(26,24,22,0.05)] bg-surface">
            {related.length === 0 ? (
              <div className="flex flex-col items-center gap-3 px-5 py-8 text-center">
                <IconTile category={tool.category} icon={tool.icon} size="md" />
                <div>
                  <div className="text-[13.5px] font-semibold tracking-[-0.15px] text-ink">
                    {t.tfMoreEmptyTitle}
                  </div>
                  <p className="mt-1 text-[12px] leading-[1.45] text-muted [text-wrap:pretty]">
                    {t.tfMoreEmptyDesc}
                  </p>
                </div>
              </div>
            ) : (
              related.map((rt) => (
                <Link
                  key={rt.slug}
                  href={`/tools/${rt.slug}`}
                  onClick={(e) => {
                    if (rt.isPro && !isPro) {
                      e.preventDefault();
                      openProModal();
                    }
                  }}
                  className="flex items-center gap-3 border-b border-[rgba(26,24,22,0.07)] px-4 py-[13px] transition-colors last:border-b-0 hover:bg-[rgba(17,17,17,0.02)]"
                >
                  <IconTile category={rt.category} icon={rt.icon} size="sm" />
                  <div className="min-w-0 flex-1">
                    <div className="truncate text-[13.5px] font-semibold tracking-[-0.15px]">
                      {rt.name}
                    </div>
                    <div className="truncate text-[11.5px] text-muted">{rt.desc}</div>
                  </div>
                  {rt.isPro ? <Badge variant="pro" size="sm" label={t.badgePro} /> : null}
                  <ChevronRight size={15} className="flex-none text-muted" />
                </Link>
              ))
            )}
          </div>
        </aside>
      </div>

      {/* Mobile action bar */}
      <div className="fixed inset-x-0 bottom-0 z-[60] flex gap-2.5 border-t border-[rgba(26,24,22,0.07)] bg-[rgba(254,252,253,0.9)] px-4 pb-[calc(12px_+_env(safe-area-inset-bottom))] pt-3 backdrop-blur-[18px] md:hidden">
        <ResetButton full label={t.tfReset} disabled={generating} onReset={reset} />
        <GenerateButton
          full
          label={generating ? t.tfGenerating : t.tfGenerate}
          disabled={!canGenerate || generating}
          busy={generating}
          onGenerate={() => void generate()}
        />
      </div>
    </>
  );
}
